import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import ItemBuilder from '../../features/item-builder/ItemBuilder';
import useAIMessaging from '../../features/messaging/useAIMessaging.hooks';
import useHypothesisFrames from '../../hooks/useHypothesisFrames.hooks';
import useUnresolvedItemsSummary from '../../hooks/useUnresolvedItemsSummary.hooks';
import useUnresolvedItemAnchors from '../../hooks/useUnresolvedItemAnchors.hooks';
import useSessionStatus from '../../hooks/sessionStatus/useSessionStatus.hooks';
import useWebSocketAlert from '../../hooks/useWebSocketAlert.hooks';
import {
  considerTimePeriods,
  considerTimePeriodsForUnifiedMenu,
} from '../../reducers/menuSlice';
import { selectCartModality } from '../../selectors/cart';
import {
  selectActiveRestaurantTimezone,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { ICartItemRefs, SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import { TIME_PERIOD_CHECK_INTERVAL } from '../../utils/constants';
import AIStatusBar from '../AIStatusBar';
import IntentsSection from '../IntentsSection';
import RightBar from '../Layout/RightBar';
import { OmniBar } from '../OmniBar/OmniBar';
import SlashCommandReminder from '../SlashCommandReminder';
import Banner from '../Banner';
import TranscriptSliderArea from '../TranscriptSliderArea';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import FinalOrderBackdrop from '../FinalOrderBackdrop';
import {
  selectFeatureFlagFinalOrderValidation,
  selectInfoSlashCommandOptions,
} from '../../redux/features/config/config.selector';
import { useReminder } from '../../contexts/ReminderContext';
import TaskLoadingMetricsDisplay from '../TaskLoadingMetricsDisplay';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';

interface IMainContentStyleProps {
  showAIStatus: boolean;
}

const useStyles = makeStyles<Theme, IMainContentStyleProps>(({ spacing }) => ({
  mainContentClass: {
    height: '100%',
    display: 'flex',
    backgroundColor: Colors.porcelain,
  },
  flexHeightContainerClass: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderTop: 'none',
    position: 'relative',
  },
  leftMiddlePaneContainerClass: {
    position: 'relative',
    display: 'flex',
    width: `calc(100% - 360px)`,
  },
  leftPaneClass: {
    display: 'flex',
    height: 'auto',
    width: '360px',
    wordBreak: 'break-word',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
  },
  // Bottom-pinned column in the left pane: AI suggestions sit above the intent
  // buttons. zIndex is above the absolute transcript (zIndex 5) so the transient
  // suggestion card paints over the transcript's bottom edge.
  leftBottomStackClass: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignSelf: 'flex-end',
    position: 'relative',
    zIndex: 6,
  },
  middlePaneClass: {
    height: '100%',
    width: `calc(100% - 360px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  rightPaneContainerClass: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    width: '360px',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
}));

const MainContent = () => {
  const { isAIActive, isAIAutoMode } = useAIMessaging();
  const showAIStatus = isAIActive && isAIAutoMode;
  const {
    reminderMessage,
    isVisible: showReminder,
    hideReminder,
  } = useReminder();
  const { lastSentMetrics } = useTaskLoadingMetricsContext();
  const {
    mainContentClass,
    leftPaneClass,
    leftBottomStackClass,
    middlePaneClass,
    leftMiddlePaneContainerClass,
    rightPaneContainerClass,
    flexHeightContainerClass,
    alertClass,
  } = useStyles({ showAIStatus });
  const dispatch = useAppDispatch();
  const timezone = useAppSelector(selectActiveRestaurantTimezone);
  const stage = useAppSelector(selectedStageSelector);
  const modality = useAppSelector(selectCartModality);
  const infoSlashCommands = useAppSelector(selectInfoSlashCommandOptions);
  const eventWSConnectionMessage = useAppSelector(
    (state) => state.messages.eventWSConnectionMessage
  );
  const audioWSConnectionMessage = useAppSelector(
    (state) => state.messages.audioWSConnectionMessage
  );
  const eventWSConnectionSuccess = useAppSelector(
    (state) => state.messages.eventWSConnectionSuccess
  );
  const audioWSConnectionSuccess = useAppSelector(
    (state) => state.messages.audioWSConnectionSuccess
  );
  const cartItemRefs = useRef<ICartItemRefs>({});
  const setCartItemRef: SetCartItemRef = (
    cartItemId: number,
    ref: HTMLDivElement | null
  ) => {
    cartItemRefs.current[cartItemId] = ref;
  };
  const FEATURE_FLAG_FINAL_ORDER_VALIDATION = useAppSelector(
    selectFeatureFlagFinalOrderValidation
  );

  const { showAlert: showEventAlert, handleCloseAlert: handleCloseEventAlert } =
    useWebSocketAlert({
      wsConnectionMessage: eventWSConnectionMessage,
      wsConnectionSuccess: eventWSConnectionSuccess,
    });

  const { showAlert: showAudioAlert, handleCloseAlert: handleCloseAudioAlert } =
    useWebSocketAlert({
      wsConnectionMessage: audioWSConnectionMessage,
      wsConnectionSuccess: audioWSConnectionSuccess,
    });

  useEffect(() => {
    if (timezone) {
      const timer = setInterval(() => {
        if (getMenuUnificationFeatureFlag()) {
          dispatch(
            considerTimePeriodsForUnifiedMenu({ timezone, infoSlashCommands })
          );
        } else {
          dispatch(considerTimePeriods({ modality, timezone, stage }));
        }
      }, TIME_PERIOD_CHECK_INTERVAL);
      return () => {
        clearInterval(timer);
      };
    }
  }, [timezone, dispatch, modality, stage, infoSlashCommands]);

  // HITL-1749: rebuild the item list as soon as info_slash_commands or
  // its exclude list changes — Unleash usually resolves after the menu has
  // loaded — instead of waiting for the next rollover tick.
  useEffect(() => {
    if (timezone && getMenuUnificationFeatureFlag()) {
      dispatch(
        considerTimePeriodsForUnifiedMenu({ timezone, infoSlashCommands })
      );
    }
  }, [timezone, dispatch, infoSlashCommands]);

  useHypothesisFrames();
  useUnresolvedItemsSummary();
  useUnresolvedItemAnchors();
  useSessionStatus();

  return (
    <div id="main-content" className={mainContentClass}>
      <div id="left-middle-pane" className={leftMiddlePaneContainerClass}>
        <div id="left-pane" className={leftPaneClass}>
          <TranscriptSliderArea />
          <div className={leftBottomStackClass}>
            <IntentsSection />
          </div>
        </div>
        <div id="middle-pane" className={middlePaneClass}>
          <div className={flexHeightContainerClass}>
            <TaskLoadingMetricsDisplay metrics={lastSentMetrics} />
            <Banner />
            {showEventAlert && eventWSConnectionMessage && (
              <Alert
                variant="filled"
                severity={eventWSConnectionSuccess ? 'success' : 'error'}
                className={alertClass}
                onClose={
                  eventWSConnectionSuccess ? handleCloseEventAlert : undefined
                }
              >
                Event WebSocket: {eventWSConnectionMessage}
              </Alert>
            )}
            {showAudioAlert && audioWSConnectionMessage && (
              <Alert
                variant="filled"
                severity={audioWSConnectionSuccess ? 'success' : 'error'}
                className={alertClass}
                onClose={
                  audioWSConnectionSuccess ? handleCloseAudioAlert : undefined
                }
                sx={{
                  top:
                    showEventAlert && eventWSConnectionMessage
                      ? '72px'
                      : undefined,
                }}
              >
                Audio WebSocket: {audioWSConnectionMessage}
              </Alert>
            )}

            <ItemBuilder setCartItemRef={setCartItemRef} />
          </div>
          {showAIStatus && <AIStatusBar />}
          <SlashCommandReminder
            message={reminderMessage}
            isVisible={showReminder}
            onHide={hideReminder}
          />
          <OmniBar />
        </div>
      </div>
      <div id="right-pane" className={rightPaneContainerClass}>
        <RightBar />
      </div>
      {FEATURE_FLAG_FINAL_ORDER_VALIDATION && <FinalOrderBackdrop />}
    </div>
  );
};

export default MainContent;
